<script setup lang="ts">
import PlusMenuLink from '@layout/nav-v2/mobile/components/PlusMenuLink.vue';
import {
  URL_OFFER_CREATE,
  URL_USER_PROFILE_MANAGE,
} from '@tools/routes/routes';
import { trans } from '@elements/translation.ts';
import Icon from '@components/Icon.vue';
import { storeToRefs } from 'pinia';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Divider from '@components/Divider.vue';
import IconUserCircle from '@icons/ph_user-circle.svg';
import IconPhoneCall from '@icons/ph_phone-call.svg';
import IconBuildings from '@icons/ph_buildings.svg';
import IconPlus from '@icons/ph_plus.svg';
import IconMapPin from '@icons/ph_map-pin.svg';
import { computed } from 'vue';
import { PhoneNumber } from '@tools/phoneNumber.ts';
import Typography from '@components/Typography.vue';
import PlusMenuItem from '@layout/nav-v2/mobile/components/PlusMenuItem.vue';
import { MenuItemGroup as MenuItemGroupEnum } from '@enums/menuItemGroups.ts';
import PlusMenuItemGroup from '@layout/nav-v2/mobile/components/PlusMenuItemGroup.vue';
import MobileLocationsMenu from '@layout/nav-v2/mobile/components/menus/MobileLocationsMenu.vue';

const navbarStore = useNavbarStore();
const { salesPhone, internationalSalesPhone } = storeToRefs(navbarStore);
const {
  isCurrentDropdownOpen,
  setCurrentDropdownOpen,
  openLoginModal,
  closePlusMenu,
} = navbarStore;

const companyName = computed<string>(() => window.COMPANY_NAME);

const handleOpenLoginModal = (): void => {
  closePlusMenu();
  openLoginModal();
};
</script>

<template>
  <li>
    <PlusMenuLink :link="`tel:${internationalSalesPhone}`">
      <template #icon>
        <Icon
          :svg="IconPhoneCall"
          width="24"
          height="24"
          fill="gray-darker"
        />
      </template>

      <template #text>
        <div class="flex gap-x-1">
          <Typography variant="body">
            {{ trans('user_menu.call_company', { company_name: companyName }) }}
          </Typography>

          <Typography variant="body-bold">
            {{ salesPhone }}
          </Typography>
        </div>
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink :link="URL_OFFER_CREATE">
      <template #icon>
        <Icon
          :svg="IconPlus"
          width="24"
          height="24"
          fill="gray-darker"
        />
      </template>

      <template #text>
        {{ trans('user_menu.main.create_offer') }}
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuItem @click="handleOpenLoginModal">
      <template #icon>
        <Icon
          :svg="IconUserCircle"
          width="24"
          height="24"
          stroke="gray-darker"
          has-icon-stroke
        />
      </template>

      <template #text>
        {{ trans('user_menu.login_register') }}
      </template>
    </PlusMenuItem>
  </li>

  <li class="my-2 px-3">
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuItemGroup
      :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.LOCATIONS)"
      @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.LOCATIONS : null)"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconMapPin"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.top.locations') }}
      </template>

      <template #submenus>
        <MobileLocationsMenu class="mt-4" />
      </template>
    </PlusMenuItemGroup>
  </li>

  <li class="mt-2 px-3">
    <Divider class="m-0" color="gray" />
  </li>
</template>
